import { getFullUrl } from "./config";
import type {
  OrderListingResponse,
  OrderDetailsResponse,
  PrescriptionResponse,
  PrescriptionData,
  LabTestOptionsResponse,
  MedicineOptionsResponse,
  DosageFormatResponse,
  DoctorListingResponse,
  AdminApproveOrderModel,
  CancelOrderModel,
  AdminPrepareOrderModel,
  PatientDetailsByMobileResponse,
  RegisterPatientRequest,
  RegisterPatientResponse,
  AddPatientAddressRequest,
  NearestClinicResponse,
  OrderSummaryRequest,
  OrderSummaryResponse,
} from "./admin-order.types";

// ============================================================
// Admin Order Management — Service Layer
// ============================================================
export class AdminOrderManagementService {
  private static getToken(): string | null {
    return localStorage.getItem("token");
  }

  private static headers(): Record<string, string> {
    return {
      "Content-Type": "application/json",
      Authorization: `Bearer ${this.getToken()}`,
    };
  }

  // ── Listing ──────────────────────────────────────────────
  static async getOrderListing(
    page: number = 1,
    pageSize: number = 10,
    orderStatus: string,
    clinicId?: string,
    searchText?: string
  ): Promise<OrderListingResponse> {
    const params = new URLSearchParams({
      page: page.toString(),
      pageSize: pageSize.toString(),
      status: orderStatus,
    });
    if (clinicId) {
      params.append("clinicId", clinicId);
    }
    if (searchText) {
      params.append("searchText", searchText);
    }
    const url = `${getFullUrl("/orderservice/admin-order/order-listing")}?${params}`;
    const res = await fetch(url, { method: "GET", headers: this.headers() });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
  }

  // ── Order Details ────────────────────────────────────────
  static async getOrderDetails(orderId: number): Promise<OrderDetailsResponse> {
    const url = getFullUrl(`/orderservice/admin-order/order-details/${orderId}`);
    const res = await fetch(url, { method: "GET", headers: this.headers() });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
  }

  // ── Prescriptions ───────────────────────────────────────
  static async getOrderPrescriptions(orderId: number): Promise<PrescriptionResponse> {
    const url = getFullUrl(`/orderservice/admin-order/order-prescription/${orderId}`);
    const res = await fetch(url, { method: "GET", headers: this.headers() });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
  }

  // ── Generate Prescription ───────────────────────────────
  static async generatePrescription(data: PrescriptionData): Promise<{ success: boolean; message: string }> {
    const url = getFullUrl("/orderservice/admin-order/generate-prescription");
    const res = await fetch(url, {
      method: "POST",
      headers: this.headers(),
      body: JSON.stringify(data),
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
  }

  // ── Lab Test Search ─────────────────────────────────────
  static async searchLabTests(searchText: string): Promise<LabTestOptionsResponse> {
    const params = new URLSearchParams({ searchText });
    const url = `${getFullUrl("/inventoryorderservice/labtest/doctor/options")}?${params}`;
    const res = await fetch(url, { method: "GET", headers: this.headers() });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
  }

  // ── Medicine Search ─────────────────────────────────────
  static async searchMedicines(searchText: string): Promise<MedicineOptionsResponse> {
    const params = new URLSearchParams({ searchText });
    const url = `${getFullUrl("/inventoryorderservice/product/doctor/product-options")}?${params}`;
    const res = await fetch(url, { method: "GET", headers: this.headers() });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
  }

  // ── Dosage Text Formatting ──────────────────────────────
  static async generateDosageText(text: string): Promise<DosageFormatResponse> {
    const url = getFullUrl("/doctorservice/doctor/notes/generate-text");
    const res = await fetch(url, {
      method: "POST",
      headers: this.headers(),
      body: JSON.stringify({ text }),
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
  }

  // ── Doctor Listing (for assignment) ─────────────────────
  static async getDoctorListing(name: string = ""): Promise<DoctorListingResponse> {
    const params = new URLSearchParams();
    if (name) params.set("name", name);
    const url = `${getFullUrl("/doctorservice/doctor/prescription/listing")}?${params}`;
    const res = await fetch(url, { method: "GET", headers: this.headers() });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
  }

  // ── Book Appointment for Doctor ─────────────────────────
  static async bookDoctorAppointment(
    orderId: number,
    patientId: string,
    doctorId: string
  ): Promise<{ success: boolean; message: string; appointmentId?: string }> {
    const url = getFullUrl("/orderservice/admin-order/book-appointment-for-doctor");
    const res = await fetch(url, {
      method: "POST",
      headers: this.headers(),
      body: JSON.stringify({ orderId, patientId, doctorId }),
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
  }

  // ── Cancel / Reject Order ───────────────────────────────
  static async cancelOrder(data: CancelOrderModel): Promise<{ success: boolean; message: string }> {
    const url = getFullUrl("/orderservice/online-order/cancel-order");
    const res = await fetch(url, {
      method: "POST",
      headers: this.headers(),
      body: JSON.stringify(data),
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
  }

  // ── Approve Order ───────────────────────────────────────
  static async approveOrder(data: AdminApproveOrderModel): Promise<{ success: boolean; message: string }> {
    const url = getFullUrl("/orderservice/admin-order/approve-order");
    const res = await fetch(url, {
      method: "POST",
      headers: this.headers(),
      body: JSON.stringify(data),
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
  }

  // ── Clinic Stock (for replacement) ──────────────────────
  static async getClinicStock(
    clinicId: string,
    page: number = 1,
    pageSize: number = 15,
    searchText: string = ""
  ): Promise<{ success: boolean; data: unknown }> {
    const params = new URLSearchParams({
      page: page.toString(),
      pageSize: pageSize.toString(),
      clinicId,
    });
    if (searchText) params.set("searchText", searchText);
    const url = `${getFullUrl("/inventoryorderservice/stock/get/clinic-stock")}?${params}`;
    const res = await fetch(url, { method: "GET", headers: this.headers() });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
  }

  // ── Check product validation ─────────────────────────────
  static async checkProduct(productId: string): Promise<{ success: boolean; doctorNameReq: boolean }> {
    const params = new URLSearchParams({ productId });
    const url = `${getFullUrl("/inventoryorderservice/order/check-product")}?${params}`;
    const res = await fetch(url, { method: "GET", headers: this.headers() });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
  }

  // ── Fetch Patient and Family Details ──────────────────────
  static async getPatientAndFamilyDetails(patientId: string): Promise<any> {
    const url = getFullUrl(`/patientservice/patient/fetch/patientandfamilydetails/${patientId}`);
    const res = await fetch(url, { method: "GET", headers: this.headers() });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
  }

  // ── Update Ordered For ───────────────────────────────────
  static async updateOrderedFor(orderId: number, orderForId: string): Promise<{ success: boolean; message: string }> {
    const url = getFullUrl("/orderservice/admin-order/add-ordered-for");
    const res = await fetch(url, {
      method: "PUT",
      headers: this.headers(),
      body: JSON.stringify({ orderId, orderForId }),
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
  }

  // ── Upload Prescription ─────────────────────────────────
  static async uploadPrescription(orderId: number, files: File[]): Promise<{ success: boolean; message: string }> {
    const url = getFullUrl(`/orderservice/admin-order/upload-prescription/${orderId}`);
    const formData = new FormData();
    files.forEach((file) => formData.append("image", file));
    const res = await fetch(url, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${this.getToken()}`,
      },
      body: formData,
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
  }

  // ── Prepare Order ────────────────────────────────────────
  static async prepareOrder(data: AdminPrepareOrderModel): Promise<{ success: boolean; message: string }> {
    const url = getFullUrl("/orderservice/admin-order/prepare-order");
    const res = await fetch(url, {
      method: "POST",
      headers: this.headers(),
      body: JSON.stringify(data),
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
  }

  // ── Return Order Listing ──────────────────────────────────
  static async getReturnOrderListing(
    page: number = 1,
    pageSize: number = 10,
    status?: string,
    clinicId?: string,
    searchText?: string
  ): Promise<any> {
    const params = new URLSearchParams({
      page: page.toString(),
      pageSize: pageSize.toString(),
    });
    if (status) params.append("status", status);
    if (clinicId) params.append("clinicId", clinicId);
    if (searchText) params.append("searchText", searchText);

    const url = `${getFullUrl("/orderservice/admin-order/return-order-listing")}?${params.toString()}`;
    const res = await fetch(url, { method: "GET", headers: this.headers() });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
  }

  // ── Return Order Details ──────────────────────────────────
  static async getReturnDetails(orderId: number): Promise<any> {
    const url = getFullUrl(`/orderservice/return/details/${orderId}`);
    const res = await fetch(url, { method: "GET", headers: this.headers() });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
  }

  // ── Clinic Return Verification ────────────────────────────
  static async verifyReturn(data: {
    returnId: number;
    orderId: number;
    status: string;
    deliveryPersonId?: string;
    cancellationReason?: string;
    products?: Array<{
      productId: string;
      returnItemStatus: string;
      rejectionReason?: string;
      itemsCount?: number;
    }>;
  }): Promise<{ success: boolean; message: string }> {
    const url = getFullUrl("/orderservice/return/clinic/verify");
    const res = await fetch(url, {
      method: "PUT",
      headers: this.headers(),
      body: JSON.stringify(data),
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
  }

  // ── Update Inventory & Initiate Refund ────────────────────
  static async updateInventoryAndRefund(data: {
    returnId: number;
    products: Array<{
      productId: string;
      inwardId: string;
      itemsCount: number;
    }>;
  }): Promise<{ success: boolean; message: string }> {
    const url = getFullUrl("/orderservice/return/clinic/update-inventory");
    const res = await fetch(url, {
      method: "POST",
      headers: this.headers(),
      body: JSON.stringify(data),
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
  }

  // ════════════════════════════════════════════════════════════
  // ── Create New Order — API Methods ─────────────────────────
  // ════════════════════════════════════════════════════════════

  // ── Verify Patient by Mobile ─────────────────────────────
  static async getPatientDetailsByMobile(mobileNumber: string): Promise<PatientDetailsByMobileResponse> {
    const params = new URLSearchParams({ mobileNumber });
    const url = `${getFullUrl("/patientservice/patient/details-by-mobile")}?${params}`;
    const res = await fetch(url, { method: "GET", headers: this.headers() });
    return res.json();
  }

  // ── Register New Patient ─────────────────────────────────
  static async registerNewPatient(data: RegisterPatientRequest): Promise<RegisterPatientResponse> {
    const url = getFullUrl("/authservice/auth/register");
    const res = await fetch(url, {
      method: "POST",
      headers: this.headers(),
      body: JSON.stringify(data),
    });
    return res.json();
  }

  // ── Add Patient Address ──────────────────────────────────
  static async addPatientAddress(data: AddPatientAddressRequest): Promise<{ success: boolean; message: string; data?: any }> {
    const url = getFullUrl("/patientservice/patient/address/add");
    const res = await fetch(url, {
      method: "POST",
      headers: this.headers(),
      body: JSON.stringify(data),
    });
    return res.json();
  }

  // ── Get Nearest Clinic ───────────────────────────────────
  static async getNearestClinic(params: {
    patientAddress: string;
    pincode: string;
    latitude: number;
    longitude: number;
  }): Promise<NearestClinicResponse> {
    const searchParams = new URLSearchParams({
      patientAddress: params.patientAddress,
      pincode: params.pincode,
      latitude: params.latitude.toString(),
      longitude: params.longitude.toString(),
    });
    const url = `${getFullUrl("/orderservice/online-order/get-nearest-clinic")}?${searchParams}`;
    const res = await fetch(url, { method: "GET", headers: this.headers() });
    return res.json();
  }

  // ── Add Family Member ────────────────────────────────────
  static async addFamilyMember(formData: FormData): Promise<{ success: boolean; message: string; data?: any }> {
    const url = getFullUrl("/patientservice/patient/add/familymember");
    const res = await fetch(url, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${this.getToken()}`,
      },
      body: formData,
    });
    return res.json();
  }

  // ── Get Order Summary ────────────────────────────────────
  static async getOrderSummary(data: OrderSummaryRequest): Promise<OrderSummaryResponse> {
    const url = getFullUrl("/inventoryorderservice/product/order-summary");
    const res = await fetch(url, {
      method: "POST",
      headers: this.headers(),
      body: JSON.stringify(data),
    });
    return res.json();
  }

  // ── Create Admin Medicine Order ──────────────────────────
  static async createAdminMedicineOrder(formData: FormData): Promise<{ success: boolean; message: string; data?: any }> {
    const url = getFullUrl("/orderservice/admin-order/create-medicine-order");
    const res = await fetch(url, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${this.getToken()}`,
      },
      body: formData,
    });
    return res.json();
  }
}
